import React, { useState, useEffect, useRef } from 'react'
import { UserWalletApi } from './../apis/UserWallet'
import { useToast } from './../providers/ToastProvider'
import './ManageBilling.css'
import BillingComponent from '../components/billing/BillingComponent'
import { GpModalShell, GpButton } from '../components/gp'

const UpdateCardModal = (props) => {
    const [activeDelete, setActiveDelete] = useState(false)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [cardInfo, setCardInfo] = useState()
    const [showError, setShowError] = useState(false)
    const { showToast } = useToast()
    const billingRef = useRef()

    const handleUpdateCard = () => {
        if (billingRef.current) {
            billingRef.current.handleSubmit()
        }
    }

    const startDelete = () => {
        setShowError(false)
        setActiveDelete(!activeDelete)
    }

    const finishDelete = () => {
        setShowError(false)
        setDeleting(true)
        UserWalletApi.delete(cardInfo?.id)
            .then(() => {
                setActiveDelete(false)
                setDeleting(false)
                props.reloadWallet()
                props.handleClose()
            })
            .catch(() => {
                setActiveDelete(false)
                setDeleting(false)
                setShowError(true)
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: 'Something went wrong',
                })
            })
    }

    const handleModalClose = () => {
        setActiveDelete(false)
        props.handleClose()
    }

    const creditCardAdded = () => {
        props.reloadWallet()
        setTimeout(() => {
            handleModalClose()
        }, 3000)
    }

    useEffect(() => {
        if (props.cardInfo) {
            setCardInfo(props.cardInfo)
        }
    }, [props.cardInfo])

    const card = props.cardInfo?.card || {}
    const last4 = card.last4 || '••••'
    const brand = (card.brand || 'CARD').toUpperCase()
    const isDefault = props.cardInfo?.isDefault
    const hasError = props.cardInfo?.metadata?.cardOk === false

    // Preview-chip color variants mirror the billing-page PaymentCard:
    //   primary   → dark ink + lime accents
    //   secondary → surface bg + ink + accent green
    //   error     → red gradient + white text
    const dark = isDefault || hasError
    const previewBg = hasError
        ? 'linear-gradient(135deg, #8F1E1E 0%, #B42222 100%)'
        : isDefault
            ? 'var(--gp-ink)'
            : 'var(--gp-surface)'
    const previewColor = dark ? '#FFFFFF' : 'var(--gp-ink)'
    const previewBorder = dark ? 'none' : '1px solid var(--gp-hairline)'
    const roleLabel = hasError
        ? 'CARD ERROR'
        : isDefault
            ? 'PRIMARY · ON FILE'
            : 'SECONDARY'
    const roleColor = hasError
        ? '#FFFFFF'
        : isDefault
            ? 'var(--gp-lime)'
            : 'var(--gp-ink-mute)'
    const brandColor = hasError
        ? '#FFFFFF'
        : isDefault
            ? 'var(--gp-lime)'
            : 'var(--gp-accent)'
    const numberDotsColor = dark ? 'inherit' : 'var(--gp-ink-mute)'
    const radial = isDefault
        ? 'radial-gradient(circle, rgba(182,242,115,0.18), transparent 65%)'
        : hasError
            ? 'radial-gradient(circle, rgba(255,255,255,0.12), transparent 65%)'
            : 'radial-gradient(circle, rgba(71,132,105,0.10), transparent 65%)'

    return (
        <GpModalShell
            open={props.open}
            onClose={handleModalClose}
            title={activeDelete ? 'Delete card' : 'Update card'}
            width={580}
            footer={
                activeDelete ? (
                    <>
                        <GpButton variant="ghost" onClick={startDelete} disabled={deleting}>
                            Cancel
                        </GpButton>
                        <GpButton
                            variant="danger"
                            onClick={finishDelete}
                            disabled={deleting}
                            data-testid="cypress-billing-delete-card-confirm-button"
                        >
                            {deleting ? 'Deleting…' : 'Yes, delete'}
                        </GpButton>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={startDelete}
                            disabled={saving}
                            data-testid="cypress-billing-delete-card-button"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 44,
                                padding: '0 18px',
                                borderRadius: 999,
                                background: 'transparent',
                                border: '1px solid var(--gp-danger-border)',
                                color: 'var(--gp-danger)',
                                font: '500 14px/1 var(--gp-font)',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                opacity: saving ? 0.5 : 1,
                                marginRight: 'auto',
                                transition: 'background 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#FDF3F3'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent'
                            }}
                        >
                            Delete card
                        </button>
                        <GpButton variant="ghost" onClick={handleModalClose} disabled={saving}>
                            Cancel
                        </GpButton>
                        <GpButton variant="primary" onClick={handleUpdateCard} disabled={saving}>
                            {saving ? 'Updating…' : 'Update'}
                        </GpButton>
                    </>
                )
            }
        >
            {!activeDelete && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Card preview chip — primary / secondary / error variants */}
                    <div
                        style={{
                            background: previewBg,
                            color: previewColor,
                            border: previewBorder,
                            borderRadius: 'var(--gp-r-md)',
                            padding: '16px 18px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 14,
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                right: -40,
                                top: -40,
                                width: 140,
                                height: 140,
                                borderRadius: '50%',
                                background: radial,
                                pointerEvents: 'none',
                            }}
                        />
                        <div
                            style={{
                                font: '500 11px/1 var(--gp-font)',
                                letterSpacing: 0.8,
                                color: roleColor,
                                position: 'relative',
                            }}
                        >
                            {roleLabel}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                position: 'relative',
                                gap: 14,
                            }}
                        >
                            <div
                                style={{
                                    font: '500 18px/1 var(--gp-mono)',
                                    letterSpacing: 1.2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    color: numberDotsColor,
                                }}
                            >
                                <span style={{ opacity: 0.55 }}>••••</span>
                                <span style={{ opacity: 0.55 }}>••••</span>
                                <span style={{ opacity: 0.55 }}>••••</span>
                                <span style={{ color: previewColor }}>{last4}</span>
                            </div>
                            <div
                                style={{
                                    font: '600 13px/1 var(--gp-font)',
                                    letterSpacing: 1.2,
                                    color: brandColor,
                                }}
                            >
                                {brand}
                            </div>
                        </div>
                    </div>

                    {showError && (
                        <div className="card-error">
                            Oops. Something went wrong. Please try again later.
                        </div>
                    )}

                    <BillingComponent
                        isUpdate={true}
                        ref={billingRef}
                        cardInfo={props.cardInfo?.card}
                        setParentLoading={setSaving}
                        billingAddress={props.cardInfo?.billingDetails}
                        creditCardAdded={creditCardAdded}
                    />
                </div>
            )}

            {activeDelete && (
                <div style={{ padding: '8px 0 4px' }}>
                    <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                        Are you sure you want to delete this card?
                    </div>
                    <div
                        style={{
                            font: 'var(--gp-caption)',
                            color: 'var(--gp-ink-mute)',
                            marginTop: 6,
                        }}
                    >
                        The card ending {last4} will be removed from your account. You'll need to
                        re-add it to use it again.
                    </div>
                </div>
            )}
        </GpModalShell>
    )
}

export default UpdateCardModal
